<template>
    <v-card class="change-password-card" elevation="8">
        <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="timeout" top>
            {{ snackbarInfo }}
            <v-btn dark text @click="snackbar = false">关闭</v-btn>
        </v-snackbar>

        <div class="card-header">
            <h2 class="title">修改密码</h2>
            <v-icon 
                v-if="!$store.state.persistent" 
                @click="$store.state.changePassword = false" 
                class="close-icon" 
            >x</v-icon>
        </div>

        <div v-if="$store.state.persistent" class="warning-banner">
            <!-- <v-icon class="warning-icon">warning</v-icon> -->
            <p class="warning-text">
                为保障您的数据安全，统一交付平台密码应至少包含大写字母、小写字母、数字、特殊字符其中的三种，且长度不能小于8位。系统检测到您的密码属于<span class="warning-highlight">弱密码</span>，烦请修改。
            </p>
        </div>

        <div class="password-form">
            <v-container>
                <v-row>
                    <v-col cols="12">
                        <v-text-field
                            v-model="oldPwd"
                            :type="oldPwdShow ? 'text' : 'password'"
                            label="原密码"
                            placeholder="请输入原密码"
                            class="password-input"
                            :rules="[rules.required]"
                            :append-icon="oldPwdShow ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="oldPwdShow = !oldPwdShow"
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-text-field
                            v-model="newPwd"
                            :type="newPwdShow ? 'text' : 'password'"
                            label="新密码"
                            placeholder="请输入新密码"
                            class="password-input"
                            :rules="[rules.required, rules.minLength, rules.maxLength, rules.noSpace]"
                            :error-messages="newPwdError"
                            :append-icon="newPwdShow ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="newPwdShow = !newPwdShow"
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <v-text-field
                            v-model="newPwdAgain"
                            type="password"
                            label="确认新密码"
                            placeholder="请再次输入新密码"
                            class="password-input"
                            :rules="[rules.required, confirmPasswordRule]"
                        ></v-text-field>
                    </v-col>
                </v-row>

                <v-row v-if="newPwd" class="password-strength">
                    <v-col cols="12">
                        <p class="strength-label">密码强度：</p>
                        <div class="strength-bars">
                            <div 
                                v-for="(bar, index) in 4" 
                                :key="index" 
                                class="strength-bar"
                                :class="getStrengthClass(index)"
                            ></div>
                            <span class="strength-text">{{ getStrengthText() }}</span>
                        </div>
                    </v-col>
                </v-row>

                <v-row class="button-row">
                    <v-col cols="12" class="text-center">
                        <v-btn 
                            class="submit-btn" 
                            @click="changePwd"
                            :disabled="!isFormValid"
                        >
                            确认修改
                        </v-btn>
                    </v-col>
                </v-row>
            </v-container>
        </div>
    </v-card>
</template>

<script>
import { JSEncrypt } from 'jsencrypt'
export default {
    name: "ChangePassword",
    data() {
        return {
            snackbar: false,
            snackbarColor: "",
            snackbarInfo: "",
            timeout: 3000,

            oldPwd: "",
            newPwd: "",
            newPwdAgain: "",
            oldPwdShow: false,
            newPwdShow: false,

            rules: {
                required: value => !!value || "该项不能为空",
                minLength: value => value.length >= 8 || "密码长度不能小于8位",
                maxLength: value => value.length <= 20 || "密码长度不能超过20位",
                noSpace: value => !value.includes(' ') || "密码中不能包含空格",
            },
        }
    },
    computed: {
        confirmPasswordRule() {
            return value => value === this.newPwd || "两次输入的密码不一致";
        },
        isFormValid() {
            return this.oldPwd && this.newPwd && this.newPwdAgain && this.newPwd === this.newPwdAgain && this.isValidPassword(this.newPwd) && this.newPwd !== this.oldPwd;
        },
        passwordStrength() {
            let score = 0;
            if (this.newPwd.length >= 8) score++;
            if (/[a-z]/.test(this.newPwd)) score++;
            if (/[A-Z]/.test(this.newPwd)) score++;
            if (/[0-9]/.test(this.newPwd)) score++;
            if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(this.newPwd)) score++;
            return Math.min(Math.floor(score / 1.25), 4);
        },
        newPwdError() {
            if (!this.newPwd) return '';
            if (this.newPwd === this.oldPwd) return '新密码不能与原密码相同';
            return '';
        }
    },
    methods: {
        isValidPassword(password) {
            const regex = /^(?![a-zA-Z]+$)(?![A-Z0-9]+$)(?![A-Z\W_!@#$%^&*`~()-+=]+$)(?![a-z0-9]+$)(?![a-z\W_!@#$%^&*`~()-+=]+$)(?![0-9\W_!@#$%^&*`~()-+=]+$)[a-zA-Z0-9\W_!@#$%^&*`~()-+=]{8,}$/;
            return regex.test(password);
        },

        getStrengthClass(index) {
            const strength = this.passwordStrength;
            if (index < strength) {
                if (strength === 1) return 'weak';
                if (strength === 2) return 'medium';
                if (strength === 3) return 'strong';
                return 'very-strong';
            }
            return 'empty';
        },

        getStrengthText() {
            const strength = this.passwordStrength;
            if (strength === 0) return '请输入密码';
            if (strength === 1) return '弱';
            if (strength === 2) return '中等';
            if (strength === 3) return '强';
            return '非常强';
        },

        async changePwd() {
            if (!this.oldPwd || !this.newPwd || !this.newPwdAgain) {
                this.showError("请补充全部信息！");
                return;
            }

            if (this.newPwd !== this.newPwdAgain) {
                this.showError("两次输入新密码不一致！");
                return;
            }

            if (this.newPwd.includes('DELETE') || this.newPwd.includes('INSERT') || 
                this.newPwd.includes('UPDATE') || this.newPwd.includes('SELECT') ||
                this.newPwd.toLowerCase().includes('delete') || this.newPwd.toLowerCase().includes('insert') ||
                this.newPwd.toLowerCase().includes('update') || this.newPwd.toLowerCase().includes('select') ||
                this.newPwd.includes('<') || this.newPwd.includes('>') || this.newPwd.includes('&') ||
                this.newPwd.includes("'") || this.newPwd.includes('"')) {
                this.showError("请输入规范密码,禁止包含尖括号、引号、&符以及增删改查关键字符！");
                return;
            }

            if (!this.isValidPassword(this.newPwd)) {
                this.showError("密码应同时包含大写字母、小写字母、数字、特殊字符中的三种，并大于等于8位！");
                return;
            }

            const params = new FormData();
        

            if (this.$store.state.account !== null) {
                // params.account = this.$store.state.account;
            }
            // 加密密码
            const publicKey = await this.getPublicKey()
            params.append('rsaParam', this.encryptData(this.$store.state.account+'|'+this.oldPwd+'|'+this.newPwd+'|'+Date.now(), publicKey));

            const headers = {};
            const token = localStorage.getItem('token') || this.$store.state.user.token;
            const loginToken = localStorage.getItem('loginToken') || this.$store.state.user.loginToken;
            if (token) {
                headers['Authorization'] = token;
            }
            if (loginToken) {
                headers['LoginToken'] = loginToken;
            }

            this.axios.post('/loginapi/changePwd', params)
                .then((res) => {
                    if (res.data.message === "success") {
                        localStorage.setItem('pwd', this.isValidPassword(this.newPwd));
                        localStorage.removeItem('userId');
                        localStorage.removeItem('token');
                        localStorage.removeItem('loginToken');
                        localStorage.removeItem('userName');
                        localStorage.removeItem('forceChangePwd');
                        this.$store.state.user.userId = '';
                        this.$store.state.user.userName = '';
                        this.$store.state.user.token = '';
                        this.$store.state.user.loginToken = '';
                        this.$store.state.user.logined = false;
                        this.$store.state.user.isBigCustomer = false;
                        this.$store.state.user.authorizationCode = '';
                        this.$store.state.user.obsKey = '';
                        this.$store.state.user.forceChangePwd = false;
                        this.$store.state.changePassword = false;
                        this.$store.state.persistent = false;
                        this.$store.state.account = null;
                        if (this.$router.history.current.path !== "/") {
                            this.$router.push("/");
                        }
                    } else {
                        this.showError(res.data.data);
                    }
                })
                .catch(() => {
                    this.showError("网络异常，请稍后重试！");
                });
        },

            // RSA 加密
    encryptData(data, publicKey) {
      console.log('要加密的数据为：' + data)
      if (!publicKey) {
        throw new Error('公钥为空，无法加密')
      }
      try {
        const encrypt = new JSEncrypt()
        encrypt.setPublicKey(publicKey)
        const encrypted = encrypt.encrypt(data)
        if (!encrypted) {
          throw new Error('加密失败')
        }
        return encrypted
      } catch (error) {
        console.error('加密过程出错:', error)
        throw error
      }
    },

    // 获取 RSA 公钥
    async getPublicKey() {
      try {
        const res = await this.axios.get('/loginapi/getRsaPubKey')
        console.log('获取到的公钥为：' + (res.data.publicKey))
        if (res.status === 200) {
          return res.data.publicKey
        } else {
          this.showMessage(res.data.message || '获取公钥失败')
          const error = new Error(res.data.message)
          error._handled = true
          throw error
        }
      } catch (error) {
        if (!error._handled) {
          this.showMessage('获取公钥请求失败，请检查网络')
        }
        throw error
      }
    },

        showError(message) {
            this.snackbarColor = "error";
            this.snackbarInfo = message;
            this.snackbar = true;
        }
    }
}
</script>

<style scoped>
.change-password-card {
    width: 500px;
    border-radius: 10px;
    overflow: hidden;
    background: #ffffff;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 28px;
    border-bottom: 1px solid #e8e8e8;
    background: linear-gradient(225.45deg, rgba(50,197,255,1) -0.21%,rgba(20,66,170,1) 101.4%);
}

.title {
    font-size: 18px;
    font-weight: 500;
    color: #ffffff;
    margin: 0;
}

.close-icon {
    width: 24px;
    height: 24px;
    cursor: pointer;
    opacity: 0.8;
    transition: opacity 0.2s;
    color: #ffffff;
    font-size: 22px;
}

.close-icon:hover {
    opacity: 1;
}

.warning-banner {
    display: flex;
    align-items: flex-start;
    padding: 14px 28px;
    background: #fffbeb;
    border-bottom: 1px solid #fef3c7;
}

.warning-icon {
    color: #f59e0b;
    margin-right: 10px;
    margin-top: 2px;
    flex-shrink: 0;
}

.warning-text {
    font-size: 13px;
    color: #92400e;
    margin: 0;
    line-height: 1.5;
}

.warning-highlight {
    color: #dc2626;
    font-weight: 600;
}

.password-form {
    padding: 24px 28px;
}

.password-input {
    margin-bottom: 20px;
}

.password-input .v-input__control {
    border-radius: 8px;
}

.password-input .v-text-field--outlined fieldset {
    border-color: #d9d9d9;
    transition: border-color 0.2s;
}

.password-input .v-text-field--outlined:focus-within fieldset {
    border-color: #32c5ff;
}

.password-strength {
    margin-bottom: 16px;
}

.strength-label {
    font-size: 13px;
    color: #666666;
    margin: 0 0 8px 0;
}

.strength-bars {
    display: flex;
    align-items: center;
}

.strength-bar {
    width: 55px;
    height: 6px;
    border-radius: 3px;
    margin-right: 6px;
    transition: background-color 0.3s;
}

.strength-bar.empty {
    background-color: #e8e8e8;
}

.strength-bar.weak {
    background-color: #ef4444;
}

.strength-bar.medium {
    background-color: #f59e0b;
}

.strength-bar.strong {
    background-color: #10b981;
}

.strength-bar.very-strong {
    background-color: #06b6d4;
}

.strength-text {
    margin-left: 10px;
    font-size: 13px;
    font-weight: 500;
    color: #666666;
}

.button-row {
    margin-top: 8px;
}

.submit-btn {
    width: 100%;
    height: 44px;
    font-size: 15px;
    font-weight: bold;
    border-radius: 8px;
    background: linear-gradient(225.45deg, rgba(50,197,255,1) -0.21%,rgba(20,66,170,1) 101.4%);
    color: #ffffff;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
    transition: box-shadow 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
}

.submit-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.v-snackbar {
    border-radius: 8px;
}
</style>